import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import { FolderIcon } from '../../../components/Icon';
import Tooltip from '../../../components/Tooltip';
import { formatDate } from '../../../utils';
import type { ContractListItem } from '../ManageContracts';

interface UseContractsColumnsOptions {
  onViewDetails: (contractId: string) => void;
}

function formatCorName(firstName: string | null | undefined, lastName: string | null | undefined): string {
  if (!firstName && !lastName) {
    return '—';
  }

  if (lastName && firstName) {
    return `${lastName}, ${firstName}`;
  }

  return lastName || firstName || '—';
}

export function useContractsColumns({ onViewDetails }: UseContractsColumnsOptions) {
  return useMemo<ColumnDef<ContractListItem>[]>(
    () => [
      {
        accessorKey: 'contractNumber',
        enableSorting: true,
        header: 'Contract',
        size: 168,
      },
      {
        accessorKey: 'companyName',
        enableSorting: true,
        header: 'Company',
        size: 260,
      },
      {
        accessorKey: 'contractStartDate',
        cell: ({ getValue }) => formatDate(getValue<string>()),
        enableSorting: true,
        header: 'Start Date',
        size: 108,
      },
      {
        accessorKey: 'contractEndDate',
        cell: ({ getValue }) => formatDate(getValue<string>()),
        enableSorting: true,
        header: 'End Date',
        size: 108,
      },
      {
        accessorFn: (row) => formatCorName(row.primaryCorFirstName, row.primaryCorLastName),
        cell: ({ getValue, row }) => (
          <div className='manage-contracts__cor-cell'>
            <span>{getValue<string>()}</span>
            {row.original.isDelegatedToCurrentUser && (
              <Tooltip
                data-testid={`manage-contracts-delegated-tooltip-${row.original.id}`}
                placement='top'
                title='You are the active Delegate Federal Approver while the Primary Federal Approver is out of office.'>
                <span className='manage-contracts__delegated-badge'>Delegated to you</span>
              </Tooltip>
            )}
          </div>
        ),
        enableSorting: true,
        header: 'Contracting Officer Representative (COR)',
        id: 'corName',
        size: 260,
      },
      {
        cell: ({ row }) => (
          <div className='manage-contracts__action-cell'>
            <button
              aria-label={`View contract ${row.original.contractNumber} details`}
              className='manage-contracts__action-button action-icon--blue-square'
              title='View Details'
              type='button'
              onClick={() => row.original.id && onViewDetails(row.original.id)}>
              <FolderIcon />
            </button>
          </div>
        ),
        enableSorting: false,
        header: 'Action',
        id: 'action',
        size: 105,
      },
    ],
    [onViewDetails],
  );
}
